import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import reportWebVitals from './components/.shared/reportWebVitals';
import { SourceRedirect } from './utils/SourceRedirect';
import { SourceProvider } from './utils/sourceContext';
import { UserEngagementProvider } from './utils/userEngagementContext';
import { InterviewProvider } from './utils/interviewContext';
import { PostHogTracker } from './components/PostHogTracker/PostHogTracker';
import { TidioPosthogBridge } from './utils/tidioPosthogBridge';
import { ConsentGuard } from './utils/ConsentGuard';

import './index.css';
import 'animate.css';
import Privacy from './components/Privacy/Privacy';
import Terms from './components/Terms/Terms';
import CookiePolicyPage from './components/CookiePolicy/CookiePolicy';
import SuccessStoriesPage from './components/SuccessStories/SuccessStories';
import Contact from './components/ContactUs/Contact';
import Welcome from './components/Welcome/Welcome';
import CheckIn from './components/CheckIn/CheckIn';
import Intake from './components/Intake/Intake';
import ArticlesPage from './components/Articles/Articles';
import Landing from './components/Landing/Landing';
import SafeAdsLanding from './components/Landing/SafeAdsLanding';
import KnowledgeCenter from './components/KnowledgeCenter/KnowledgeCenter';
import AboutUs from './components/AboutUs/AboutUs';
import Login from './components/Login/Login';
import Patients from './components/Patients/Patients';
import Checkout from './components/Checkout/Checkout';
import PaymentSuccess from './components/PaymentSuccess/PaymentSuccess';
import Billing from './components/Billing/Billing';
import InterviewEntry from './components/Interview/InterviewEntry';
import InterviewGlp1Known from './components/Interview/InterviewGlp1Known';
import InterviewDiet from './components/Interview/InterviewDiet';
import InterviewMovement from './components/Interview/InterviewMovement';
import InterviewGlp1History from './components/Interview/InterviewGlp1History';
import InterviewHeightWeight from './components/Interview/InterviewHeightWeight';
import InterviewBestYou from './components/Interview/InterviewBestYou';
import InterviewPriority from './components/Interview/InterviewPriority';
import ProgramsV22 from './components/ProgramsV22/ProgramsV22';
import ProgramsV22ExperimentEntry from './components/ProgramsV22ExperimentEntry/ProgramsV22ExperimentEntry';
import ResultV22 from './components/ResultV22/ResultV22';
import ResultV22ExperimentEntry from './components/ResultV22ExperimentEntry/ResultV22ExperimentEntry';
import CEOLanding from './components/CustomLandings/CEOLanding/CEOLanding';
import AffiliateLanding from './components/AffiliateProgram/AffiliateLanding';
import GLP1VettedVendorLanding from './components/CustomLandings/GLP1VettedVendorLanding/GLP1VettedVendorLanding';
import BHVettedVendorLanding from './components/CustomLandings/BHVettedVendorLanding/BHVettedVendorLanding';
import YourJourneyBegins from './components/CustomLandings/YourJourneyBegins/YourJourneyBegins';
import SemaglutideLanding from './components/CustomLandings/SemaglutideLanding/SemaglutideLanding';
import BaseLandingV2 from './components/CustomLandings/BaseLandingV2/BaseLandingV2';
import BenefitHubExperimentEntry from './components/BenefitHubExperimentEntry/BenefitHubExperimentEntry';
import BaseLandingV3 from './components/CustomLandings/BaseLandingV3/BaseLandingV3';
import BaseLandingV4 from './components/CustomLandings/BaseLandingV4/BaseLandingV4';
import BaseLandingV5 from './components/CustomLandings/BaseLandingV5/BaseLandingV5';

const App = () => {
  return (
    <InterviewProvider>
      <UserEngagementProvider>
          <BrowserRouter>
            <SourceProvider>
              <SourceRedirect />
              <PostHogTracker />
              <TidioPosthogBridge />
              <ConsentGuard>
                <Routes>
                  <Route path="login" element={<Login />} />
                  <Route path="patients" element={<Patients />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="packages" element={<ProgramsV22ExperimentEntry />} />
                  <Route path="products" element={<ProgramsV22ExperimentEntry />} />
                  <Route path="programs" element={<ProgramsV22ExperimentEntry />} />
                  <Route path="programs-v2-2" element={<ProgramsV22 />} />
                  <Route path="products-v2-2" element={<ProgramsV22 />} />
                  <Route path="articles" element={<ArticlesPage />} />
                  <Route path="success-stories" element={<SuccessStoriesPage />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="welcome" element={<Welcome />} />
                  <Route path="checkout" element={<Navigate to="/checkout/payment" replace />} />
                  <Route path="checkout/payment" element={<Checkout />} />
                  <Route path="checkout/contact" element={<Navigate to="/checkout/payment" replace />} />
                  <Route path="payment-success" element={<PaymentSuccess />} />
                  <Route path="intake" element={<Intake />} />
                  <Route path="checkin" element={<CheckIn />} />
                  <Route path="start" element={<ProgramsV22ExperimentEntry />} />
                  <Route path="interview/" element={<InterviewEntry />} />
                  <Route path="interview/glp1-known" element={<InterviewGlp1Known />} />
                  <Route path="interview/height-weight" element={<InterviewHeightWeight />} />
                  <Route path="interview/bestyou" element={<InterviewBestYou />} />
                  <Route path="interview/priority" element={<InterviewPriority />} />
                  <Route path="interview/diet" element={<InterviewDiet />} />
                  <Route path="interview/movement" element={<InterviewMovement />} />
                  <Route path="interview/glp1-history" element={<InterviewGlp1History />} />
                  <Route path="result" element={<ResultV22ExperimentEntry />} />
                  <Route path="result-v2-2" element={<ResultV22 />} />
                  <Route path="research" element={<KnowledgeCenter />} />
                  <Route path="safety" element={<KnowledgeCenter />} />
                  <Route path="faq" element={<KnowledgeCenter />} />
                  <Route path="research-center" element={<KnowledgeCenter />} />
                  <Route path="safety-center" element={<KnowledgeCenter />} />
                  <Route path="knowledge-center" element={<KnowledgeCenter />} />
                  <Route path="about" element={<AboutUs />} />
                  <Route path="about-us" element={<AboutUs />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="landing" element={<Landing />} />
                  <Route path="glp1-vetted-vendor" element={<GLP1VettedVendorLanding />} />
                  <Route path="benefithub" element={<BenefitHubExperimentEntry />} />
                  <Route path="benefithub-vetted-vendor" element={<BHVettedVendorLanding />} />
                  <Route path="ceo_struggle" element={<CEOLanding />} />
                  <Route path="semaglutide" element={<SemaglutideLanding />} />
                  <Route path="base-landing-v2" element={<BaseLandingV2 />} />
                  <Route path="base-landing-v3" element={<BaseLandingV3 />} />
                  <Route path="base-landing-v4" element={<BaseLandingV4 />} />
                  <Route path="base-landing-v5" element={<BaseLandingV5 />} />
                  <Route path="your-journey-begins" element={<YourJourneyBegins />} />
                  <Route path="affiliates" element={<AffiliateLanding />} />
                  <Route path="cookie-policy" element={<CookiePolicyPage />} />
                  <Route path="medical-weight-loss" element={<SafeAdsLanding />} />
                  <Route path="online-evaluation" element={<SafeAdsLanding />} />
                  <Route path="treatment-pricing" element={<SafeAdsLanding />} />
                  <Route path="*" element={<Landing />} />
                </Routes>
              </ConsentGuard>
            </SourceProvider>
          </BrowserRouter>
      </UserEngagementProvider>
    </InterviewProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

reportWebVitals(console.log);
